import { getIconByContentType } from 'core/utils/lessonItems';
import { stripHtml } from 'core/utils/utils';
import PropTypes from 'prop-types';
import React from 'react';
import Accordion from 'ui/molecules/Accordion/Accordion';
import AssetImage from 'ui/atoms/AssetImage/AssetImage';
import Dialog from 'ui/organisms/dialogs/Dialog/Dialog';
import Hr from 'ui/atoms/Hr/Hr';
import Icon from 'ui/atoms/Icon/Icon';
import SelectCustomizable from 'ui/atoms/SelectCustomizable/SelectCustomizable';
import Spinner from 'ui/atoms/Spinner/Spinner';
import { useDialogAddReferenceMaterial } from './useDialogAddReferenceMaterial';

function DialogAddReferenceMaterial({ isOpen, onClose, onSubmit, course, lesson }) {
  const { resources, submodules, selectedSubmodule, t, closeDialog, submitDialog, onChangeSubmodule, foldersData, isLibraryView, loading } =
    useDialogAddReferenceMaterial({
      onClose,
      onSubmit,
      course,
      lesson,
    });

  function renderItem(resource) {
    function handleSelectResource() {
      submitDialog(resource);
    }
    return (
      <button
        type="button"
        key={resource.id || resource.guid || resource.contentGuid}
        className="dialog-add-reference-material__item"
        onClick={handleSelectResource}
      >
        <Icon name={getIconByContentType(resource.content_type_guid)} color="grey-03" size="xl" />{' '}
        <span className="dialog-add-reference-material__item-name">{stripHtml(resource?.name || resource?.content_name || '')}</span>
      </button>
    );
  }

  function renderFolderTitle(folder) {
    return (
      <span className="dialog-add-reference-material__folder-title">
        {folder.folder_name} ({folder.items?.length || 0})
      </span>
    );
  }

  function renderLibraryContent() {
    const folderTabs = foldersData
      .filter((folder) => folder.items?.length > 0)
      .map((folder) => ({
        title: renderFolderTitle(folder),
        children: (
          <div className="dialog-add-reference-material__folder-items">
            {folder.items.filter((item) => item.visibility === 'Students').map((item) => renderItem(item))}
          </div>
        ),
      }));

    return (
      <>
        {folderTabs.length > 0 && (
          <div className="dialog-add-reference-material__folders">
            <Accordion isMulti position="left" tabs={folderTabs} />
          </div>
        )}
        {resources.filter((resource) => resource.visibility === 'Students').map((resource) => renderItem(resource))}
      </>
    );
  }

  function renderItems() {
    return resources.map((resource) => renderItem(resource));
  }

  const hasFoldersWithItems = foldersData.some((folder) => folder.items?.length > 0);
  const isEmpty = !loading && resources.length === 0 && !hasFoldersWithItems;

  return (
    <Dialog
      className="dialog-add-reference-material"
      title={t('common:Add reference material')}
      size="xl"
      isOpen={isOpen}
      onClose={closeDialog}
      customHeader={
        <div className="dialog-add-reference-material__header">
          <div className="dialog-add-reference-material__header-select">
            <SelectCustomizable
              options={submodules}
              onChange={onChangeSubmodule}
              value={selectedSubmodule}
              defaultValue={submodules.filter((submodule) => submodule.guid === lesson.guid)}
            />
          </div>
          <Hr />
        </div>
      }
    >
      <div className="dialog-add-reference-material__container">
        {loading && (
          <div className="dialog-add-reference-material__loading">
            <Spinner />
          </div>
        )}
        {!loading && isLibraryView && renderLibraryContent()}
        {!loading && !isLibraryView && renderItems()}
        {isEmpty && (
          <div className="dialog-add-reference-material__empty">
            <AssetImage className="dialog-add-reference-material__empty-image" id={'noMaterials'} alt={t('common:No contents found')} />
            <div className="dialog-add-reference-material__empty-text">{t('common:No contents found')}</div>
          </div>
        )}
      </div>
    </Dialog>
  );
}

DialogAddReferenceMaterial.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  course: PropTypes.object.isRequired,
  lesson: PropTypes.object.isRequired,
};

export default DialogAddReferenceMaterial;
